// Historique des opérations : journal d'audit filtrable et vérification d'intégrité de la chaîne.
import { Bandeau, Bouton, Champ, DateChamp, EtatVide, Panneau, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import type { EntreeAudit, Liste, VerificationAudit } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure, versIso } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import styles from './administration.module.css';
import commun from '../../commun/commun.module.css';

const TYPES_OBJETS = [
  { valeur: '', libelle: 'Tous les objets' },
  { valeur: 'compte', libelle: 'Comptes' },
  { valeur: 'session', libelle: 'Sessions' },
  { valeur: 'appartenance', libelle: 'Appartenances et rôles' },
  { valeur: 'role', libelle: 'Rôles' },
  { valeur: 'habilitation', libelle: 'Habilitations' },
  { valeur: 'delegation', libelle: 'Délégations' },
  { valeur: 'parametre', libelle: 'Paramètres' },
  { valeur: 'module_actif', libelle: 'Modules' },
  { valeur: 'courrier', libelle: 'Courriers' },
  { valeur: 'dossier', libelle: 'Dossiers' },
  { valeur: 'document', libelle: 'Documents' },
];

function Details({ e }: { e: EntreeAudit }) {
  if (!e.avant && !e.apres && !e.contexte) return <span className={commun.na}>aucun détail</span>;
  return (
    <details>
      <summary>Voir le détail</summary>
      {e.avant ? <pre className={styles.json}>Avant : {JSON.stringify(e.avant, null, 2)}</pre> : null}
      {e.apres ? <pre className={styles.json}>Après : {JSON.stringify(e.apres, null, 2)}</pre> : null}
      {e.contexte ? <pre className={styles.json}>Contexte : {JSON.stringify(e.contexte, null, 2)}</pre> : null}
      <p className={commun.na}>Empreinte : {e.empreinte}</p>
    </details>
  );
}

export function PageHistorique() {
  const [objetType, setObjetType] = useState('');
  const [depuis, setDepuis] = useState('');
  const [limite, setLimite] = useState('100');
  const [filtres, setFiltres] = useState({ objetType: '', depuis: '', limite: '100' });
  const journal = useQuery({
    queryKey: ['audit', 'journal', filtres],
    queryFn: ({ signal }) =>
      requete<Liste<EntreeAudit>>('/audit', {
        signal,
        parametres: { objet_type: filtres.objetType, depuis: filtres.depuis ? versIso(filtres.depuis) : undefined, limite: filtres.limite },
      }).then((r) => r.elements),
  });
  const verification = useAction({ executer: () => requete<VerificationAudit>('/audit/verification') });
  const [resultat, setResultat] = useState<VerificationAudit | null>(null);

  const colonnes: ColonneTableau<EntreeAudit>[] = [
    { id: 'rang', entete: 'Rang', numerique: true, cellule: (e) => String(e.rang), valeurTri: (e) => Number(e.rang) },
    { id: 'date', entete: 'Date', cellule: (e) => dateHeure(e.horodatage), valeurTri: (e) => e.horodatage, enTeteLigne: true, masquable: false },
    { id: 'action', entete: 'Action', cellule: (e) => e.action, valeurTri: (e) => e.action },
    { id: 'objet', entete: 'Objet', cellule: (e) => (e.objet_type ? `${e.objet_type}${e.objet_id ? ` ${e.objet_id.slice(0, 8)}` : ''}` : 'non renseigné') },
    { id: 'auteur', entete: 'Compte', cellule: (e) => (e.compte_id ? e.compte_id.slice(0, 8) : 'système') },
    { id: 'details', entete: 'Détails', cellule: (e) => <Details e={e} /> },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Historique des opérations"
        description="Journal en écriture seule : chaque entrée est chaînée à la précédente par une empreinte, ce qui rend toute altération détectable."
      />
      <Panneau titre="Intégrité du journal" niveau={2}>
        <div className={commun.ligne}>
          <Bouton
            icone={ShieldCheck}
            variante="secondaire"
            chargement={verification.enCours}
            libelleChargement="Vérification en cours"
            onClick={() => void verification.lancerAsync(undefined).then(setResultat).catch(() => setResultat(null))}
          >
            Vérifier l&apos;intégrité
          </Bouton>
        </div>
        {verification.messageErreur ? <Bandeau type="erreur">{verification.messageErreur}</Bandeau> : null}
        {resultat ? (
          resultat.intacte ? (
            <Bandeau type="succes" titre="Chaîne intègre">
              {resultat.entrees_verifiees.toLocaleString('fr-FR')} entrées vérifiées, aucune rupture.
            </Bandeau>
          ) : (
            <Bandeau type="erreur" titre="Rupture détectée">
              Première rupture au rang {resultat.rupture?.rang ?? 'inconnu'} : {resultat.rupture?.motif ?? 'motif non précisé'}. Signalez-la immédiatement au
              responsable de la sécurité.
            </Bandeau>
          )
        ) : null}
      </Panneau>
      <form
        className={commun.ligne}
        style={{ alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault();
          setFiltres({ objetType, depuis, limite });
        }}
        aria-label="Filtres du journal"
      >
        <Selection libelle="Type d'objet" value={objetType} onChange={(e) => setObjetType(e.target.value)} options={TYPES_OBJETS} />
        <DateChamp libelle="Depuis" avecHeure value={depuis} onChange={(e) => setDepuis(e.target.value)} facultatif />
        <Champ libelle="Nombre d'entrées" type="number" min={1} max={500} value={limite} onChange={(e) => setLimite(e.target.value)} />
        <Bouton type="submit">Filtrer</Bouton>
      </form>
      <EtatRequete chargement={journal.isPending} erreur={journal.error} onReessayer={() => void journal.refetch()} fonction="Le journal des opérations">
        {(journal.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune opération">
            <p>Aucune entrée ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Journal des opérations" colonnes={colonnes} lignes={journal.data ?? []} cleLigne={(e) => e.id} cleColonnes="journal" />
        )}
      </EtatRequete>
    </>
  );
}
